import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'
import {
  SHIFTS, DAY_LABELS, ASSIGNED_ROLES, DEFAULT_TEMPLATES,
  generateDailyTasksFromTemplates, todayISO,
} from '../../lib/tasks'

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7]

/**
 * Carry a template edit onto the task already generated for today.
 * Best effort: if the shift column isn't migrated yet the title/description
 * still update, and the shift simply stays null (which reads as "any").
 */
async function syncTodaysTask(recipientId, previousTitle, payload) {
  const patch = {
    title: payload.title,
    description: payload.description,
    shift: payload.shift,
  }
  const match = supabase
    .from('tasks')
    .update(patch)
    .eq('care_recipient_id', recipientId)
    .eq('due_date', todayISO())
    .eq('title', previousTitle)
  const { error } = await match
  if (!error) return
  const { shift, ...withoutShift } = patch
  await supabase
    .from('tasks')
    .update(withoutShift)
    .eq('care_recipient_id', recipientId)
    .eq('due_date', todayISO())
    .eq('title', previousTitle)
}
const blank = () => ({
  title: '', description: '', shift: 'morning',
  days_of_week: [...ALL_DAYS], assigned_role: 'caregiver', is_active: true,
})

/**
 * Recurring caregiver schedule. Shared by the admin panel and the profile page
 * so there is one implementation of the template rules.
 */
export default function TaskTemplates({ recipientId, canManage }) {
  const { user } = useAuth()
  const toast = useToast()

  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [editing, setEditing] = useState(null) // id | 'new' | null
  const [form, setForm] = useState(blank)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('task_templates').select('*')
      .eq('care_recipient_id', recipientId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true })
    // Hide the section entirely until the migration has been run.
    if (error) setUnavailable(true)
    else setList(data || [])
    setLoading(false)
  }, [recipientId])

  useEffect(() => { load() }, [load])

  function startNew() { setForm(blank()); setEditing('new') }
  function startEdit(t) {
    setForm({
      title: t.title || '', description: t.description || '',
      shift: t.shift || 'any', days_of_week: t.days_of_week || [...ALL_DAYS],
      assigned_role: t.assigned_role || 'caregiver', is_active: t.is_active !== false,
    })
    setEditing(t.id)
  }

  async function save() {
    if (!form.title.trim()) return toast.error('Give the task a title.')
    if (!form.days_of_week.length) return toast.error('Pick at least one day.')
    setBusy(true)
    try {
      const payload = {
        care_recipient_id: recipientId,
        title: form.title.trim(),
        description: form.description.trim() || null,
        shift: form.shift,
        days_of_week: [...form.days_of_week].sort((a, b) => a - b),
        assigned_role: form.assigned_role,
        is_active: form.is_active,
      }
      const previousTitle = editing === 'new'
        ? null
        : list.find((t) => t.id === editing)?.title

      const res = editing === 'new'
        ? await supabase.from('task_templates').insert({ ...payload, created_by: user.id, sort_order: list.length })
        : await supabase.from('task_templates').update(payload).eq('id', editing)
      if (res.error) throw res.error

      // Editing a template must also move today's already-generated task, or
      // the change silently won't apply until tomorrow.
      if (previousTitle) {
        await syncTodaysTask(recipientId, previousTitle, payload)
      }

      setEditing(null)
      await load()
      // A newly added template should appear in today's list straight away.
      await generateDailyTasksFromTemplates(recipientId)
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function toggleActive(t) {
    const { error } = await supabase.from('task_templates')
      .update({ is_active: !t.is_active }).eq('id', t.id)
    if (error) return toast.error(humanizeError(error))
    setList((l) => l.map((x) => (x.id === t.id ? { ...x, is_active: !x.is_active } : x)))
  }

  async function remove(t) {
    if (!window.confirm(`Remove "${t.title}" from the schedule?`)) return
    const { error } = await supabase.from('task_templates').delete().eq('id', t.id)
    if (error) return toast.error(humanizeError(error))
    setList((l) => l.filter((x) => x.id !== t.id))
  }

  async function addDefaults(picked) {
    setBusy(true)
    try {
      const rows = picked.map((d, i) => ({
        care_recipient_id: recipientId, created_by: user.id,
        title: d.title, shift: d.shift, days_of_week: ALL_DAYS,
        assigned_role: 'caregiver', sort_order: i, is_active: true,
      }))
      const { error } = await supabase.from('task_templates').insert(rows)
      if (error) throw error
      await load()
      await generateDailyTasksFromTemplates(recipientId)
      toast.success(`${rows.length} task${rows.length > 1 ? 's' : ''} added.`)
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  if (unavailable) return null

  return (
    <div className="card stack">
      <div className="spread">
        <div className="section-title">Caregiver task schedule</div>
        {canManage && editing === null && (
          <button className="btn btn-sm btn-secondary" onClick={startNew}>+ Add</button>
        )}
      </div>

      {loading ? (
        <p className="muted" style={{ fontSize: 14, margin: 0 }}>Loading…</p>
      ) : (
        <>
          {list.length === 0 && editing !== 'new' && (
            canManage
              ? <SetupPrompt busy={busy} onAdd={addDefaults} />
              : <p className="muted" style={{ fontSize: 14, margin: 0 }}>No recurring tasks set up yet.</p>
          )}

          {list.map((t) => (
              <div key={t.id} className="stack" style={{ gap: 6, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
                <div className="spread">
                  <div className="row" style={{ gap: 8, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, opacity: t.is_active ? 1 : 0.5 }}>{t.title}</span>
                    <span className="badge">{SHIFTS.find((s) => s.value === t.shift)?.label || t.shift}</span>
                  </div>
                  {canManage && (
                    <div className="row" style={{ gap: 10, flexShrink: 0 }}>
                      <button onClick={() => toggleActive(t)} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 13 }}>
                        {t.is_active ? 'Pause' : 'Resume'}
                      </button>
                      <button onClick={() => startEdit(t)} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 13 }}>Edit</button>
                      <button onClick={() => remove(t)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13 }}>Remove</button>
                    </div>
                  )}
                </div>
                <DayDots days={t.days_of_week || []} />
              </div>
          ))}
        </>
      )}

      {editing !== null && (
        <TemplateSheet
          isNew={editing === 'new'}
          form={form} setForm={setForm}
          onSave={save} onCancel={() => setEditing(null)} busy={busy}
        />
      )}
    </div>
  )
}

/** The add/edit form lives in a bottom sheet — an inline form inside a
 *  scrolling card made the day toggles easy to lose. */
function TemplateSheet({ isNew, form, setForm, onSave, onCancel, busy }) {
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>{isNew ? 'Add task' : 'Edit task'}</h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>
        <TemplateForm form={form} setForm={setForm} onSave={onSave} onCancel={onCancel} busy={busy} />
        <div style={{ height: 8 }} />
      </div>
    </div>
  )
}

function DayDots({ days }) {
  return (
    <div className="row" style={{ gap: 4 }}>
      {DAY_LABELS.map((d) => {
        const on = days.includes(d.value)
        return (
          <span
            key={d.value}
            title={d.label}
            style={{
              width: 22, height: 22, borderRadius: '50%', fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: on ? 'var(--teal-light)' : 'transparent',
              color: on ? 'var(--teal-dark)' : 'var(--muted)',
              border: on ? 'none' : '1px solid var(--border)',
            }}
          >
            {d.short}
          </span>
        )
      })}
    </div>
  )
}

function SetupPrompt({ onAdd, busy }) {
  const [picked, setPicked] = useState([])
  const toggle = (t) =>
    setPicked((p) => (p.some((x) => x.title === t.title) ? p.filter((x) => x.title !== t.title) : [...p, t]))

  return (
    <div className="stack" style={{ gap: 8 }}>
      <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.55 }}>
        Set up the caregiver schedule — tap the tasks that apply, then adjust days
        and shifts afterwards.
      </p>
      <div className="row wrap" style={{ gap: 8 }}>
        {DEFAULT_TEMPLATES.map((t) => {
          const on = picked.some((x) => x.title === t.title)
          return (
            <button
              key={t.title} type="button"
              className={`chip${on ? ' chip-on' : ''}`}
              aria-pressed={on}
              onClick={() => toggle(t)}
            >
              {t.title}
            </button>
          )
        })}
      </div>
      <button className="btn btn-block" disabled={busy || !picked.length} onClick={() => onAdd(picked)}>
        {busy ? <span className="spinner" /> : `Add ${picked.length || ''} task${picked.length === 1 ? '' : 's'}`}
      </button>
    </div>
  )
}

function TemplateForm({ form, setForm, onSave, onCancel, busy }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const toggleDay = (d) =>
    set('days_of_week', form.days_of_week.includes(d)
      ? form.days_of_week.filter((x) => x !== d)
      : [...form.days_of_week, d])

  return (
    <div className="stack" style={{ gap: 8, padding: '10px 0' }}>
      <input className="input" placeholder="Task title *" value={form.title} onChange={(e) => set('title', e.target.value)} aria-label="Task title" />
      <input className="input" placeholder="Description (optional)" value={form.description} onChange={(e) => set('description', e.target.value)} aria-label="Description" />

      <div className="field">
        <span className="label">Shift</span>
        <div className="row wrap" style={{ gap: 8 }}>
          {SHIFTS.map((s) => (
            <button key={s.value} type="button" className={`chip${form.shift === s.value ? ' chip-on' : ''}`}
              aria-pressed={form.shift === s.value} onClick={() => set('shift', s.value)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <span className="label">Days</span>
        <div className="row" style={{ gap: 6 }}>
          {DAY_LABELS.map((d) => {
            const on = form.days_of_week.includes(d.value)
            return (
              <button
                key={d.value} type="button" onClick={() => toggleDay(d.value)}
                aria-pressed={on} aria-label={d.label}
                style={{
                  width: 36, height: 36, borderRadius: '50%', fontWeight: 700, fontSize: 13,
                  border: on ? '2px solid var(--teal)' : '1.5px solid var(--border)',
                  background: on ? 'var(--teal-light)' : 'var(--surface)',
                  color: on ? 'var(--teal-dark)' : 'var(--muted)', cursor: 'pointer',
                }}
              >
                {d.short}
              </button>
            )
          })}
        </div>
      </div>

      <div className="field">
        <span className="label">Assigned to</span>
        <div className="row wrap" style={{ gap: 8 }}>
          {ASSIGNED_ROLES.map((r) => (
            <button key={r.value} type="button" className={`chip${form.assigned_role === r.value ? ' chip-on' : ''}`}
              aria-pressed={form.assigned_role === r.value} onClick={() => set('assigned_role', r.value)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn grow" onClick={onSave} disabled={busy}>
          {busy ? <span className="spinner" /> : 'Save'}
        </button>
      </div>
    </div>
  )
}
